import { CULT_BENEFIT_ID, EDevFlag } from '@/constants';
import {
  getRetailBenefitMembership,
  // getSkuCount,
  getSkuDetails,
} from '@/queries/catalogue.queries';
import { IFulfillmentDetails, IPricing, ISKU } from '@/types';
import { useQuery, UseQueryResult } from '@tanstack/react-query';
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useAppContext } from './AppContext';
import useSessionStorage from '@/hooks/useSessionStorage';
import { ICultTrackingStats } from '@/utils/tracking/cultPromo.tracking';
import { useSearchParams } from 'react-router-dom';
import { differenceInDays } from 'date-fns';
import Cookies from 'js-cookie';
import AppConfig from '@/AppConfig';

interface IPromoContext {
  cult?: {
    available: boolean;
    availableTill: number;
    selectedCity: IPricing | null;
    setSelectedCity: (value: string | null) => void;
    showMaintenance: boolean;
    queries: {
      cultSkuDetails: UseQueryResult<ISKU, Error>;
      querySkuCount: UseQueryResult<
        {
          count: number;
          remaining: number;
        },
        Error
      >;
      querySkuMembership: UseQueryResult<
        {
          isMembershipPresent: boolean;
          fulfillments: Array<IFulfillmentDetails>;
        },
        Error
      >;
    };
    tracking: ICultTrackingStats;
  };
  setEnabledQueries: React.Dispatch<
    React.SetStateAction<{
      cultPromoDetails: boolean;
      querySkuCount: boolean;
      querySkuMembership: boolean;
    }>
  >;
}

const PromoContext = createContext<IPromoContext | null>(null);

export const PromoProvider = ({ children }: React.PropsWithChildren) => {
  const [selectedCityPricingId, setSelectedCityPricingId] = useSessionStorage<
    string | null
  >('city', null);

  const [enabledQueries, setEnabledQueries] = useState({
    cultPromoDetails: false,
    querySkuCount: false,
    querySkuMembership: true,
  });

  const queryCultSkuDetails = useQuery({
    ...getSkuDetails(),
    enabled: enabledQueries.cultPromoDetails,
  });

  const selectedCity = useMemo(() => {
    return (
      queryCultSkuDetails.data?.pricing?.find(
        (pricing: IPricing) => pricing.id === selectedCityPricingId,
      ) ?? null
    );
  }, [queryCultSkuDetails.data, selectedCityPricingId]);

  // const querySkuCount = useQuery({
  //   ...getSkuCount(CULT_SKU_ID),
  //   enabled: enabledQueries.cultPromoDetails,
  // });

  const querySkuMembership = useQuery({
    ...getRetailBenefitMembership(CULT_BENEFIT_ID),
    enabled: enabledQueries.querySkuMembership,
  });

  const { userConfig } = useAppContext();

  const [searchParams] = useSearchParams();

  const daysUntilOfferValid = Math.max(
    differenceInDays(
      new Date(Number(userConfig?.cultPromoOpenTillTime ?? 0)),
      new Date(),
    ),
    0,
  );

  const utm_source = searchParams.get('utm_source');

  const [utmSource, setUtmSource] = useSessionStorage<string | null>(
    'utm_source',
    utm_source,
  );

  useEffect(() => {
    if (utm_source) {
      setUtmSource(utm_source);
    }
  }, [utm_source, setUtmSource]);

  return (
    <PromoContext.Provider
      value={{
        setEnabledQueries,
        cult: {
          available: true,
          availableTill: userConfig?.cultPromoOpenTillTime ?? 0,
          selectedCity,
          setSelectedCity: setSelectedCityPricingId,
          showMaintenance:
            AppConfig.CULT_UNDER_MAINTENANCE === 'true' &&
            !window.devTools.isDevFlagSet(EDevFlag.OVERRIDE_MAINTENANCE_MODE),
          queries: {
            cultSkuDetails: queryCultSkuDetails,
            // querySkuCount,
            querySkuCount: null as any, // Commented out - not using as of now
            querySkuMembership,
          },
          tracking: {
            daysUntilOfferValid: daysUntilOfferValid,
            source: utmSource ?? 'unknown',
            city: selectedCity?.cityState?.cityName,
            hasSelectedAddon: false,
            appVersion: Cookies.get('appVersion') ?? '',
            platform: Cookies.get('platform') ?? '',
          },
        },
      }}
    >
      {children}
    </PromoContext.Provider>
  );
};

export const usePromo = () => {
  const context = useContext(PromoContext);
  if (context === undefined) {
    throw new Error('usePromo must be used within a PromoProvider');
  }
  return context;
};
